/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.nikam-shreyas.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "context";
import { useParams, useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDInput from "components/MDInput";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DefaultDoughnutChart from "examples/Charts/DoughnutCharts/DefaultDoughnutChart";

import CrudService from "services/cruds-service";

import { useMaterialUIController } from "context";
import MDButton from "components/MDButton";
import skill_delta_teaser_image from "assets/images/skill-delta-teaser.jpg";
import cover_letter_teaser_image from "assets/images/cover-letter-teaser.jpg";
import { Divider, Popover, Tab, Tabs } from "@mui/material";
import CourseCard from "examples/Cards/CourseCard";
import DefaultItem from "examples/Items/DefaultItem";

import breakpoints from "assets/theme/base/breakpoints";
import AssessTab from "./assess-tab";
import PrepareTab from "./prepare-tab";
import ApplyTab from "./apply-tab";
import MDEditor from "@uiw/react-md-editor";

function VisitedJob() {
  const { job_id } = useParams();
  const [job, setJob] = useState({
    _id: job_id,
    job: null,
    skills_in_profile: [],
    skills_in_job: [],
    skill_delta: [],
    skill_match_score: 0,
    cover_letter: "",
    course_outline: "",
  });
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { getCurrentUser } = useContext(AuthContext);
  const [user, setUser] = useState({});

  // Loaders
  const [skillMatchScoreLoader, setSkillMatchScoreLoader] = useState(false);
  const [courseOutlineLoader, setCourseOutlineLoader] = useState(false);
  const [coverLetterLoader, setCoverLetterLoader] = useState(false);
  const [recommendedCoursesLoader, setRecommendedCoursesLoader] =
    useState(false);
  const [feedback, setFeedback] = useState("");
  const [donutData, setDonutData] = useState({
    labels: ["Skills In Profile", "Skills To Be Learned"],
    datasets: {
      label: "Skill Count",
      backgroundColors: ["info", "error"],
      data: [80, 20],
    },
  });

  const [recommendedCourses, setRecommendedCourses] = useState([]);
  const navigate = useNavigate();

  // function to get the recommended courses
  useEffect(() => {
    async function fetchRecommendedCourses() {
      let response = await CrudService.getRecommendedCourses(job_id);

      return response;
    }
    setRecommendedCoursesLoader(true);
    fetchRecommendedCourses().then((response) => {
      setRecommendedCourses(response);
      setRecommendedCoursesLoader(false);
    });
  }, []);

  const exploreCard = (course_id) => {
    navigate("/course-content/" + course_id);
  };

  useEffect(() => {
    async function checkToken() {
      let user_id = await getCurrentUser();
      if (!user_id) {
        setIsAuthenticated(false);
        localStorage.removeItem("token");
      }
      return user_id;
    }
    checkToken().then((userId) => setUser(userId));
  }, []);

  // function to get a particular job
  useEffect(() => {
    async function fetchJobData() {
      let response = await CrudService.getVisitedJobById(job_id);
      return response;
    }
    fetchJobData()
      .then((response) => {
        setJob(response);
        return response;
      })
      .then((response) => {
        setDonutData({
          labels: ["Skills In Profile", "Skills To Be Learned"],
          datasets: {
            label: "Skill Count",
            backgroundColors: ["success", "error"],
            data: [
              response.skills_in_job.length - response.skill_delta.length,
              response.skill_delta.length,
            ],
          },
        });
      });
  }, []);

  const generateSkillMatchScore = async () => {
    setSkillMatchScoreLoader(true);
    // call the backend to get the skill delta
    const response = await CrudService.generateSkillMatchScore({
      job_id: job._id,
      profile_id: user,
    });
    setSkillMatchScoreLoader(false);
    setJob({
      ...job,
      skill_match_score: response.match_score,
      skills_in_job: response.job_description_required_skills,
      skill_delta: response.skills_to_be_learned,
      skills_in_profile: response.profile_skills,
    });
    setDonutData({
      labels: ["Skills In Profile", "Skills To Be Learned"],
      datasets: {
        label: "Skill Count",
        backgroundColors: ["success", "error"],
        data: [
          100 - response.match_score.toFixed(2),
          response.match_score.toFixed(2),
        ],
      },
    });
  };

  const generateCoverLetter = async () => {
    // call the backend to get the cover letter
    setCoverLetterLoader(true);
    const response = await CrudService.generateCoverLetter({
      job_id: job._id,
      profile_id: user,
    });
    setCoverLetterLoader(false);
    setJob({
      ...job,
      cover_letter: response,
    });
  };

  const getCourseOutline = async () => {
    // call the backend to get the course outline
    setCourseOutlineLoader(true);
    const response = await CrudService.generateCourseOutline({
      job_id: job._id,
      profile_id: user,
      feedback: feedback,
    });
    setCourseOutlineLoader(false);
    setFeedback("");
    setJob({
      ...job,
      course_outline: response,
    });
  };

  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    // A function that sets the orientation state of the tabs.
    function handleTabsOrientation() {
      return window.innerWidth < breakpoints.values.sm
        ? setTabsOrientation("vertical")
        : setTabsOrientation("horizontal");
    }

    /** 
     The event listener that's calling the handleTabsOrientation function when resizing the window.
    */
    window.addEventListener("resize", handleTabsOrientation);

    // Call the handleTabsOrientation function to set the state with the initial value.
    handleTabsOrientation();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, [tabsOrientation]);

  const handleSetTabValue = (event, newValue) => setTabValue(newValue);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          {/* Grid for the job description */}
          <Grid item sx={12} sm={12} md={6} lg={6}>
            <MDBox
              key="job_description_block"
              display="block"
              bgColor={darkMode ? "transparent" : "white"}
              color="text"
              borderRadius="xl"
              py={2}
              px={2}
              lineHeight={1.5}
              sx={{
                border: ({ borders: { borderWidth }, palette: { white } }) =>
                  darkMode ? `${borderWidth[1]} solid ${white.main}` : 0,
                fontSize: ({ typography: { size } }) => size.md,
              }}
            >
              <Grid container spacing={2}>
                <Grid item>
                  {job.job &&
                  job.job.logo_photo_url !== null &&
                  job.job.logo_photo_url != "NaN" &&
                  job.job.logo_photo_url != "" ? (
                    <MDAvatar
                      src={job.job.logo_photo_url}
                      alt="profile-image"
                      size="xl"
                    />
                  ) : (
                    <MDAvatar
                      src="https://assets.procurement.opengov.com/assets/unknown-business-logo.png"
                      alt="profile-image"
                      size="xl"
                      shadow=""
                    />
                  )}
                </Grid>
                <Grid item>
                  <MDTypography variant="h5" mt={1}>
                    {job.job && job.job.title ? job.job.title : ""}
                  </MDTypography>
                  <MDTypography variant="h6" mt={1}>
                    {job.job && job.job.company ? job.job.company : ""}
                  </MDTypography>
                </Grid>
              </Grid>

              <MDTypography variant="h6" fontWeight="medium" mt={1}>
                Job Description
              </MDTypography>
              <MDBox
                my={2}
                // make it 50% of screen height and scrollable
                style={{ height: "60vh", overflowY: "auto" }}
              >
                <div data-color-mode="light">
                  <MDEditor.Markdown
                    source={job.job && job.job.description}
                    style={{ color: "#344767", background: "transparent" }}
                    components={{
                      p: ({ node, ...props }) => (
                        <p style={{ color: "#7b809a" }} {...props} />
                      ),
                    }}
                  />
                </div>
              </MDBox>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            <Tabs
              orientation={tabsOrientation}
              value={tabValue}
              onChange={handleSetTabValue}
            >
              <Tab label="Assess" />
              <Tab label="Prepare" />
              <Tab label="Apply" />
            </Tabs>
            <MDBox
              key="job_actions_block"
              display="block"
              bgColor={darkMode ? "transparent" : "white"}
              color="text"
              borderRadius="xl"
              mt={2.5}
              py={1.875}
              px={1.875}
              lineHeight={1.5}
              sx={{
                border: ({ borders: { borderWidth }, palette: { white } }) =>
                  darkMode ? `${borderWidth[1]} solid ${white.main}` : 0,
                fontSize: ({ typography: { size } }) => size.md,
              }}
              my={2}
              style={{ height: "72vh", overflowY: "auto" }}
            >
              {tabValue == 0 && (
                <AssessTab
                  skillMatchScoreLoader={skillMatchScoreLoader}
                  job={job}
                  donutData={donutData}
                  generateSkillMatchScore={generateSkillMatchScore}
                />
              )}
              {tabValue == 1 && (
                <PrepareTab
                  job={job}
                  recommendedCoursesLoader={recommendedCoursesLoader}
                  recommendedCourses={recommendedCourses}
                  exploreCard={exploreCard}
                  feedback={feedback}
                  setFeedback={setFeedback}
                  getCourseOutline={getCourseOutline}
                  courseOutlineLoader={courseOutlineLoader}
                />
              )}
              {tabValue == 2 && (
                <ApplyTab
                  coverLetterLoader={coverLetterLoader}
                  job={job}
                  generateCoverLetter={generateCoverLetter}
                />
              )}
            </MDBox>
          </Grid>

          {/* Grid for the cover letter */}
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default VisitedJob;
